import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  CheckSquare, Wallet, Users, Calendar, FileText, Package,
  Plus, Trash2, Check, ArrowRight, Lock, Sparkles,
  Clock, AlertTriangle, X, Save, TrendingUp, Zap,
  LayoutGrid, UserCheck, Mail, ExternalLink, Loader2
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Checklist par défaut (peut être chargée depuis le backend)
const DEFAULT_CHECKLIST = [
  { id: '1', label: 'Choisir et réserver le lieu', done: false, categorie: 'Lieu' },
  { id: '2', label: 'Définir le thème et la palette couleurs', done: false, categorie: 'Décoration' },
  { id: '3', label: 'Envoyer les invitations', done: false, categorie: 'Invités' },
  { id: '4', label: 'Réserver les animations Show Événement', done: false, categorie: 'Animations' },
  { id: '5', label: 'Réserver le DJ / groupe live', done: false, categorie: 'Musique' },
  { id: '6', label: 'Confirmer le traiteur / menu', done: false, categorie: 'Food' },
  { id: '7', label: 'Faire le point sur les RSVP', done: false, categorie: 'Invités' },
  { id: '8', label: 'Préparer le plan de table', done: false, categorie: 'Invités' },
  { id: '9', label: 'Préparer le planning Jour J', done: false, categorie: 'Organisation' },
  { id: '10', label: 'Confirmer les prestataires (J-7)', done: false, categorie: 'Prestataires' },
];

const DEFAULT_BUDGET = [
  { id: 'b1', label: 'Location salle / lieu', prevu: 0, reel: 0, paye: 0, categorie: 'Lieu' },
  { id: 'b2', label: 'Animations Show Événement', prevu: 0, reel: 0, paye: 0, categorie: 'Animations' },
  { id: 'b3', label: 'DJ / Musique', prevu: 0, reel: 0, paye: 0, categorie: 'Musique' },
  { id: 'b4', label: 'Traiteur / Buffet', prevu: 0, reel: 0, paye: 0, categorie: 'Food' },
  { id: 'b5', label: 'Décoration', prevu: 0, reel: 0, paye: 0, categorie: 'Décoration' },
  { id: 'b6', label: 'Photographe', prevu: 0, reel: 0, paye: 0, categorie: 'Prestataires' },
];

const DEFAULT_PRESTATAIRES = [
  { id: 'p1', role: 'Photographe', nom: '', telephone: '', site: '', prix: 0, statut: 'en_recherche', notes: '' },
  { id: 'p2', role: 'DJ / Musique', nom: '', telephone: '', site: '', prix: 0, statut: 'en_recherche', notes: '' },
  { id: 'p3', role: 'Traiteur', nom: '', telephone: '', site: '', prix: 0, statut: 'en_recherche', notes: '' },
];

const STATUTS = {
  en_recherche: { label: 'En recherche', color: '#F59E0B' },
  contacte: { label: 'Contacté', color: '#5DBCBF' },
  reserve: { label: 'Réservé ✓', color: '#1F9D7A' },
  annule: { label: 'Annulé', color: '#EF4444' },
};

// Section non connecté
function LoginRequired() {
  return (
    <div className="text-center py-16 px-4">
      <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
        style={{ background: 'rgba(212,175,55,0.1)' }}>
        <Lock size={32} style={{ color: '#D4AF37' }} />
      </div>
      <h2 className="font-display text-2xl font-bold mb-3" style={{ color: 'var(--ink)' }}>
        Connectez-vous pour accéder à votre espace
      </h2>
      <p className="text-sm mb-8 max-w-md mx-auto" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
        Créez un compte gratuit pour sauvegarder vos checklists, suivre votre budget,
        gérer vos invités et retrouver vos documents à tout moment.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/connexion"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-sm"
          style={{ background: '#D4AF37', color: '#0B1E20' }}>
          Se connecter <ArrowRight size={16} />
        </Link>
        <Link to="/connexion?mode=register"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-sm"
          style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
          Créer un compte gratuit
        </Link>
      </div>
      {/* Preview de ce qu'ils vont trouver */}
      <div className="mt-12 grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
        {[
          { icon: CheckSquare, title: 'Checklist personnelle', desc: 'Suivez votre avancement en temps réel', color: '#5DBCBF' },
          { icon: Wallet, title: 'Budget interactif', desc: 'Prévu, réel, payé — un tableau complet', color: '#D4AF37' },
          { icon: Users, title: 'Gestion invités', desc: 'RSVP, allergies, plan de table', color: '#F4A7B2' },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} className="p-4 rounded-2xl"
              style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3"
                style={{ background: `${item.color}18` }}>
                <Icon size={18} style={{ color: item.color }} />
              </div>
              <p className="font-bold text-sm mb-1" style={{ color: 'var(--ink)' }}>{item.title}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MonOrganisationPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('checklist');
  const [eventId] = useState('default'); // À remplacer par vrai event_id sélectionné

  // États locaux
  const [checklist, setChecklist] = useState(DEFAULT_CHECKLIST);
  const [budget, setBudget] = useState(DEFAULT_BUDGET);
  const [prestataires, setPrestataires] = useState(DEFAULT_PRESTATAIRES);
  const [quotes, setQuotes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);

  // Plan de table
  const [planDeTable, setPlanDeTable] = useState(null);   // null = pas encore chargé
  const [planLoading, setPlanLoading] = useState(false);
  const [planCreating, setPlanCreating] = useState(false);
  const [newCheckItem, setNewCheckItem] = useState('');
  const [newBudgetItem, setNewBudgetItem] = useState('');

  // Chargement données
  const loadData = useCallback(async () => {
    if (!user || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [cl, bud, pres, q] = await Promise.all([
        fetch(`${BACKEND}/api/organisation/${eventId}/checklist`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/organisation/${eventId}/budget`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/organisation/${eventId}/prestataires`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/devis?client_id=${user.id}`, { headers }).then(r => r.json()),
      ]);
      if (cl.items?.length > 0) setChecklist(cl.items);
      if (bud.items?.length > 0) setBudget(bud.items);
      if (pres.items?.length > 0) setPrestataires(pres.items);
      setQuotes(q.quotes || []);
      generateSuggestions(q.quotes || [], pres.items || []);
    } catch {}
  }, [user, token, eventId]);

  // Fetch plan de table séparé (évite de bloquer le loadData principal)
  const loadPlanDeTable = useCallback(async () => {
    if (!user || !token) return;
    setPlanLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/mon-plan-de-table`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const plans = data.plans || [];
      setPlanDeTable(plans.length > 0 ? plans[0] : false); // false = chargé, aucun plan
    } catch {
      setPlanDeTable(false);
    } finally {
      setPlanLoading(false);
    }
  }, [user, token]);

  const handleCreatePlan = async () => {
    if (!user || !token) return;
    setPlanCreating(true);
    try {
      const activeQuote = quotes.find(q =>
        ['signe','acompte_paye','confirme'].includes(q.statut)
      );
      const sess = activeQuote?.session || {};
      const res = await fetch(`${BACKEND}/api/plan-de-table`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom_evenement: sess.type_evenement
            ? `${sess.type_evenement.replace('_', ' ')} de ${user.prenom || user.nom || 'Vous'}`
            : 'Mon événement',
          type_evenement: sess.type_evenement || 'mariage',
          date_evenement: sess.date_evenement || null,
          lieu: sess.adresse || '',
          nb_invites_total: (sess.nb_adultes || 0) + (sess.nb_enfants || 0),
          organisateur_nom: `${user.prenom || ''} ${user.nom || ''}`.trim(),
          organisateur_email: user.email || '',
        }),
      });
      const data = await res.json();
      if (data.plan) {
        setPlanDeTable(data.plan);
        toast.success('Plan de table créé !');
      }
    } catch {
      toast.error('Erreur lors de la création du plan');
    } finally {
      setPlanCreating(false);
    }
  };

  const generateSuggestions = (qs, pres) => {
    const suggs = [];
    const hasPhotobooth = qs.some(q =>
      (q.items || []).some(i => (i.product_nom || '').toLowerCase().includes('photobooth'))
    );
    const hasPrestaPhotographe = pres.some(p => p.role?.toLowerCase().includes('photo') && p.statut === 'reserve');

    if (!hasPhotobooth) {
      suggs.push({
        id: 's1', icon: '📸', priority: 'P0',
        title: 'Photobooth non réservé',
        desc: 'L\'animation la plus appréciée de vos invités. Réservez avant qu\'il ne soit trop tard !',
        href: '/produit/p-ph-001', cta: 'Voir le photobooth premium',
        color: '#F4A7B2',
      });
    }
    if (!hasPrestaPhotographe) {
      suggs.push({
        id: 's2', icon: '🤝', priority: 'P1',
        title: 'Photographe non réservé',
        desc: 'Nous recommandons Laurie Gerling, photographe professionnelle dans le Lot.',
        href: '/prestataires-recommandes', cta: 'Voir nos photographes recommandés',
        color: '#D4AF37',
      });
    }
    suggs.push({
      id: 's3', icon: '🎈', priority: 'P2',
      title: 'Décoration sur-mesure',
      desc: 'Arche ballons, panneau bienvenue, guirlandes... transformez votre lieu en décor de rêve.',
      href: '/fabrication', cta: 'Créer ma décoration',
      color: '#5DBCBF',
    });
    setSuggestions(suggs);
  };

  useEffect(() => { loadData(); }, [loadData]);

  // Charger le plan de table quand l'onglet est activé (lazy)
  useEffect(() => {
    if (activeTab === 'plan_table' && planDeTable === null) {
      loadPlanDeTable();
    }
  }, [activeTab, planDeTable, loadPlanDeTable]);

  // Sauvegarde
  const saveAll = async () => {
    if (!user || !token) return;
    setSaving(true);
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    try {
      await Promise.all([
        fetch(`${BACKEND}/api/organisation/${eventId}/checklist`, { method: 'POST', headers, body: JSON.stringify(checklist) }),
        fetch(`${BACKEND}/api/organisation/${eventId}/budget`, { method: 'POST', headers, body: JSON.stringify(budget) }),
        fetch(`${BACKEND}/api/organisation/${eventId}/prestataires`, { method: 'POST', headers, body: JSON.stringify(prestataires) }),
      ]);
      toast.success('Organisation sauvegardée !');
    } catch {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Stats
  const checkDone = checklist.filter(i => i.done).length;
  const checkProgress = Math.round((checkDone / Math.max(checklist.length, 1)) * 100);
  const totalPrevu = budget.reduce((s, i) => s + (parseFloat(i.prevu) || 0), 0);
  const totalPaye = budget.reduce((s, i) => s + (parseFloat(i.paye) || 0), 0);
  const presReserves = prestataires.filter(p => p.statut === 'reserve').length;

  const TABS = [
    { id: 'checklist', label: 'Checklist', icon: CheckSquare, badge: `${checkDone}/${checklist.length}` },
    { id: 'budget', label: 'Budget', icon: Wallet, badge: `${totalPrevu.toFixed(0)}€` },
    { id: 'prestataires', label: 'Prestataires', icon: Users, badge: `${presReserves}/${prestataires.length}` },
    { id: 'suggestions', label: 'Suggestions', icon: Zap, badge: suggestions.filter(s => s.priority === 'P0' || s.priority === 'P1').length.toString() },
    { id: 'plan_table', label: 'Plan de table', icon: TrendingUp, badge: '🪑' },
    { id: 'timeline', label: 'Timeline', icon: Clock, badge: '' },
    { id: 'devis', label: 'Mes devis', icon: FileText, badge: quotes.length.toString() },
  ];

  if (!user) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="container-ose">
        <LoginRequired />
      </div>
      <Footer />
      <MobileStickyBar />
    </div>
  );

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* HEADER PAGE */}
      <div style={{ background: '#0B1E20', borderBottom: '3px solid #D4AF37' }}>
        <div className="container-ose py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <Sparkles size={20} style={{ color: '#D4AF37' }} />
                <h1 className="font-display text-2xl font-bold" style={{ color: '#FFFFFF' }}>
                  Mon organisation
                </h1>
              </div>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Bonjour {user.prenom} — votre espace événementiel complet
              </p>
            </div>

            {/* Stats rapides */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-center">
                <p className="font-bold text-lg" style={{ color: '#D4AF37' }}>{checkProgress}%</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Préparation</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-lg" style={{ color: '#5DBCBF' }}>{totalPrevu.toFixed(0)}€</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Budget prévu</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-lg" style={{ color: '#1F9D7A' }}>{presReserves}/{prestataires.length}</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Prestataires</p>
              </div>
              <button
                onClick={saveAll}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: '#D4AF37', color: '#0B1E20', border: 'none', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                <Save size={14} /> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
              </button>
            </div>
          </div>

          {/* Barre progression */}
          <div className="mt-4">
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${checkProgress}%`, background: 'linear-gradient(90deg, #D4AF37, #5DBCBF)' }} />
            </div>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div style={{ background: '#FEFAF5', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 40 }}>
        <div className="container-ose">
          <div className="flex gap-0 overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 flex-shrink-0 transition-all"
                  style={{
                    borderColor: activeTab === tab.id ? '#D4AF37' : 'transparent',
                    color: activeTab === tab.id ? '#D4AF37' : 'var(--text-muted)',
                    background: 'none',
                    cursor: 'pointer',
                  }}>
                  <Icon size={15} />
                  {tab.label}
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                    style={{ background: activeTab === tab.id ? 'rgba(212,175,55,0.15)' : 'var(--border)', color: activeTab === tab.id ? '#C58B19' : 'var(--text-muted)' }}>
                    {tab.badge}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* CONTENU PAR TAB */}
      <div className="container-ose section-py">

        {/* ══ CHECKLIST ══════════════════════════════════════════════════ */}
        {activeTab === 'checklist' && (
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-xl font-bold" style={{ color: 'var(--ink)' }}>Checklist personnelle</h2>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{checkDone}/{checklist.length} points complétés</p>
              </div>
              <Link to="/guide/anniversaire-adulte#checklist"
                className="text-xs font-semibold flex items-center gap-1"
                style={{ color: '#5DBCBF' }}>
                Guide complet <ArrowRight size={11} />
              </Link>
            </div>

            <div className="space-y-2 mb-4">
              {checklist.map(item => (
                <button key={item.id}
                  onClick={() => setChecklist(prev => prev.map(i => i.id === item.id ? { ...i, done: !i.done } : i))}
                  className="w-full flex items-start gap-3 p-3.5 rounded-xl text-left transition-all"
                  style={{ background: item.done ? 'rgba(31,157,122,0.06)' : '#FFFFFF', border: `1px solid ${item.done ? 'rgba(31,157,122,0.2)' : 'var(--border)'}`, cursor: 'pointer' }}>
                  <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ borderColor: item.done ? '#1F9D7A' : 'var(--border)', background: item.done ? '#1F9D7A' : 'transparent' }}>
                    {item.done && <Check size={11} color="white" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm" style={{ color: item.done ? 'var(--text-muted)' : 'var(--ink)', textDecoration: item.done ? 'line-through' : 'none' }}>
                      {item.label}
                    </p>
                    {item.categorie && (
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>
                        {item.categorie}
                      </span>
                    )}
                  </div>
                  <X size={13} style={{ color: 'var(--border)', flexShrink: 0, marginTop: 4 }}
                    onClick={e => { e.stopPropagation(); setChecklist(prev => prev.filter(i => i.id !== item.id)); }} />
                </button>
              ))}
            </div>

            {/* Ajouter item */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newCheckItem}
                onChange={e => setNewCheckItem(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newCheckItem.trim()) { setChecklist(prev => [...prev, { id: Date.now().toString(), label: newCheckItem.trim(), done: false, categorie: 'Autre' }]); setNewCheckItem(''); } }}
                placeholder="Ajouter un point à ma checklist…"
                className="flex-1 px-4 py-2.5 rounded-xl text-sm"
                style={{ border: '1px solid var(--border)', background: '#FFFFFF', color: 'var(--ink)', outline: 'none' }}
              />
              <button
                onClick={() => { if (newCheckItem.trim()) { setChecklist(prev => [...prev, { id: Date.now().toString(), label: newCheckItem.trim(), done: false, categorie: 'Autre' }]); setNewCheckItem(''); } }}
                className="p-2.5 rounded-xl"
                style={{ background: '#D4AF37', color: '#0B1E20', border: 'none', cursor: 'pointer' }}>
                <Plus size={18} />
              </button>
            </div>
          </div>
        )}

        {/* ══ BUDGET ════════════════════════════════════════════════════ */}
        {activeTab === 'budget' && (
          <div>
            <div className="grid grid-cols-3 gap-3 mb-6 max-w-xl mx-auto">
              {[
                { label: 'Budget prévu', value: totalPrevu, color: '#D4AF37' },
                { label: 'Déjà payé', value: totalPaye, color: '#1F9D7A' },
                { label: 'Reste à payer', value: totalPrevu - totalPaye, color: (totalPrevu - totalPaye) > 0 ? '#EF4444' : '#1F9D7A' },
              ].map((t, i) => (
                <div key={i} className="p-4 rounded-2xl text-center"
                  style={{ background: '#FFFFFF', border: `1px solid ${t.color}30` }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{t.label}</p>
                  <p className="font-display text-2xl font-bold" style={{ color: t.color }}>{t.value.toFixed(0)}€</p>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}>
                <thead>
                  <tr>
                    {['Poste', 'Catégorie', 'Prévu (€)', 'Réel (€)', 'Payé (€)', '', ''].map((h, i) => (
                      <th key={i} className="text-left py-2 px-3 text-xs font-bold" style={{ color: 'var(--text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {budget.map((item) => (
                    <tr key={item.id}>
                      <td className="py-2 px-3 rounded-l-xl font-medium text-xs" style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderRight: 0 }}>
                        {item.label}
                      </td>
                      <td className="py-2 px-3 text-xs" style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderLeft: 0, borderRight: 0, color: 'var(--text-muted)' }}>
                        {item.categorie}
                      </td>
                      {['prevu', 'reel', 'paye'].map(f => (
                        <td key={f} className="py-1 px-2" style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderLeft: 0, borderRight: 0 }}>
                          <input type="number" value={item[f] || ''} placeholder="0"
                            onChange={e => setBudget(prev => prev.map(i => i.id === item.id ? { ...i, [f]: e.target.value } : i))}
                            className="w-full px-2 py-1 rounded-lg text-xs text-right"
                            style={{ border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--ink)', outline: 'none', maxWidth: '80px' }}
                          />
                        </td>
                      ))}
                      <td className="py-2 px-3 text-xs text-right font-bold" style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderLeft: 0, borderRight: 0, color: ((parseFloat(item.prevu)||0)-(parseFloat(item.paye)||0)) > 0 ? '#EF4444' : '#1F9D7A' }}>
                        {((parseFloat(item.prevu)||0)-(parseFloat(item.paye)||0)) > 0 ? `-${((parseFloat(item.prevu)||0)-(parseFloat(item.paye)||0)).toFixed(0)}€` : '✓'}
                      </td>
                      <td className="py-2 px-3 rounded-r-xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderLeft: 0 }}>
                        <button onClick={() => setBudget(prev => prev.filter(i => i.id !== item.id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2 mt-3">
              <input type="text" value={newBudgetItem} onChange={e => setNewBudgetItem(e.target.value)}
                placeholder="Ajouter un poste de dépense…"
                className="flex-1 px-4 py-2.5 rounded-xl text-sm"
                style={{ border: '1px solid var(--border)', background: '#FFFFFF', color: 'var(--ink)', outline: 'none' }}
              />
              <button onClick={() => { if (newBudgetItem.trim()) { setBudget(prev => [...prev, { id: Date.now().toString(), label: newBudgetItem.trim(), prevu: 0, reel: 0, paye: 0, categorie: 'Autre' }]); setNewBudgetItem(''); } }}
                className="p-2.5 rounded-xl"
                style={{ background: '#D4AF37', color: '#0B1E20', border: 'none', cursor: 'pointer' }}>
                <Plus size={18} />
              </button>
            </div>
          </div>
        )}

        {/* ══ PRESTATAIRES ══════════════════════════════════════════════ */}
        {activeTab === 'prestataires' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-xl font-bold" style={{ color: 'var(--ink)' }}>Mes prestataires</h2>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{presReserves} réservé(s) sur {prestataires.length}</p>
              </div>
              <Link to="/prestataires-recommandes"
                className="text-xs font-semibold flex items-center gap-1"
                style={{ color: '#D4AF37' }}>
                Voir nos recommandations <ArrowRight size={11} />
              </Link>
            </div>

            <div className="space-y-3">
              {prestataires.map(p => (
                <div key={p.id} className="card-premium p-4">
                  <div className="grid sm:grid-cols-5 gap-3 items-center">
                    <div>
                      <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>Rôle</p>
                      <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{p.role}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>Nom</p>
                      <input type="text" value={p.nom} placeholder="Nom du prestataire"
                        onChange={e => setPrestataires(prev => prev.map(i => i.id === p.id ? { ...i, nom: e.target.value } : i))}
                        className="w-full px-2 py-1.5 rounded-lg text-xs"
                        style={{ border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--ink)', outline: 'none' }}
                      />
                    </div>
                    <div>
                      <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>Téléphone</p>
                      <input type="text" value={p.telephone} placeholder="06 XX XX XX XX"
                        onChange={e => setPrestataires(prev => prev.map(i => i.id === p.id ? { ...i, telephone: e.target.value } : i))}
                        className="w-full px-2 py-1.5 rounded-lg text-xs"
                        style={{ border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--ink)', outline: 'none' }}
                      />
                    </div>
                    <div>
                      <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--text-muted)' }}>Statut</p>
                      <select value={p.statut}
                        onChange={e => setPrestataires(prev => prev.map(i => i.id === p.id ? { ...i, statut: e.target.value } : i))}
                        className="w-full px-2 py-1.5 rounded-lg text-xs"
                        style={{ border: '1px solid var(--border)', background: 'var(--bg)', color: STATUTS[p.statut]?.color || 'var(--ink)', outline: 'none' }}>
                        {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-end">
                      <button onClick={() => setPrestataires(prev => prev.filter(i => i.id !== p.id))}
                        className="p-2 rounded-lg"
                        style={{ background: 'rgba(239,68,68,0.08)', border: 'none', cursor: 'pointer', color: '#DC2626' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={() => setPrestataires(prev => [...prev, { id: Date.now().toString(), role: 'Nouveau prestataire', nom: '', telephone: '', site: '', prix: 0, statut: 'en_recherche', notes: '' }])}
                className="w-full p-3 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold"
                style={{ background: 'transparent', border: '2px dashed var(--border)', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <Plus size={16} /> Ajouter un prestataire
              </button>
            </div>
          </div>
        )}


        {/* ══ SUGGESTIONS ════════════════════════════════════════════════ */}
        {activeTab === 'suggestions' && (
          <div className="max-w-2xl mx-auto">
            <div className="mb-6">
              <h2 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--ink)' }}>Suggestions pour votre événement</h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Idées et recommandations basées sur votre organisation actuelle</p>
            </div>

            {/* Score de complétude */}
            <div className="card-premium p-5 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: checkProgress >= 85 ? 'rgba(31,157,122,0.12)' : checkProgress >= 50 ? 'rgba(212,175,55,0.12)' : 'rgba(239,68,68,0.1)' }}>
                  <TrendingUp size={18} style={{ color: checkProgress >= 85 ? '#1F9D7A' : checkProgress >= 50 ? '#D4AF37' : '#EF4444' }} />
                </div>
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Score de préparation</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {checkProgress >= 85 ? '🎉 Événement prêt !' : checkProgress >= 65 ? '👍 Bien avancé' : checkProgress >= 40 ? '⏳ En cours' : '⚠️ Très incomplet'}
                  </p>
                </div>
                <div className="ml-auto text-right">
                  <p className="font-display text-3xl font-bold"
                    style={{ color: checkProgress >= 85 ? '#1F9D7A' : checkProgress >= 50 ? '#D4AF37' : '#EF4444' }}>
                    {checkProgress}%
                  </p>
                </div>
              </div>
              <div className="w-full h-3 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                <div className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${checkProgress}%`, background: checkProgress >= 85 ? '#1F9D7A' : checkProgress >= 50 ? '#D4AF37' : '#EF4444' }} />
              </div>
              <div className="flex justify-between mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                {['Checklist', 'Budget', 'Prestataires', 'Devis'].map(s => <span key={s}>{s}</span>)}
              </div>
            </div>

            {/* Suggestions */}
            <div className="space-y-3">
              {suggestions.map(s => (
                <div key={s.id} className="card-premium p-4"
                  style={{ borderLeft: `3px solid ${s.color}` }}>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl flex-shrink-0">{s.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{s.title}</p>
                        <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                          style={{ background: s.priority === 'P0' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)', color: s.priority === 'P0' ? '#EF4444' : '#F59E0B' }}>
                          {s.priority === 'P0' ? 'Urgent' : s.priority === 'P1' ? 'Important' : 'Suggéré'}
                        </span>
                      </div>
                      <p className="text-xs mb-3" style={{ color: 'var(--text-muted)', lineHeight: '1.5' }}>{s.desc}</p>
                      <Link to={s.href}
                        className="inline-flex items-center gap-1 text-xs font-bold"
                        style={{ color: s.color }}>
                        {s.cta} <ArrowRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tendances 2026 */}
            <div className="mt-6 p-5 rounded-2xl" style={{ background: '#0B1E20' }}>
              <p className="font-display font-bold text-sm mb-3" style={{ color: '#D4AF37' }}>✨ Tendances 2026</p>
              <div className="flex flex-wrap gap-2">
                {['Dolce Vita', 'Guinguette', 'Black & Gold', 'Garden Party', 'Bohème', 'Lounge Chic'].map(t => (
                  <Link key={t} to="/guide/anniversaire-adulte"
                    className="px-3 py-1.5 rounded-xl text-xs font-medium"
                    style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {t}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}


        {/* ══ MES DEVIS ══════════════════════════════════════════════════ */}

        {/* ══ PLAN DE TABLE ════════════════════════════════════════════════ */}
        {activeTab === 'plan_table' && (
          <div className="max-w-2xl mx-auto">

            {/* ─── Chargement ─── */}
            {planLoading && (
              <div className="flex items-center justify-center py-20 gap-3"
                style={{ color: 'var(--text-muted)' }}>
                <Loader2 size={20} className="animate-spin" style={{ color: '#D4AF37' }} />
                <span className="text-sm">Chargement de votre plan…</span>
              </div>
            )}

            {/* ─── CAS 2 : pas encore de plan ─── */}
            {!planLoading && planDeTable === false && (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: 'rgba(212,175,55,0.1)' }}>
                  <LayoutGrid size={28} style={{ color: '#D4AF37' }} />
                </div>
                <h2 className="font-display text-xl font-bold mb-2"
                  style={{ color: 'var(--ink)' }}>
                  Créez votre plan de table
                </h2>
                <p className="text-sm mb-6 max-w-sm mx-auto"
                  style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
                  Organisez la disposition de votre salle, placez vos invités,
                  gérez les régimes alimentaires et envoyez vos invitations personnalisées.
                </p>

                {/* 3 mini features */}
                <div className="grid grid-cols-3 gap-3 mb-8 max-w-sm mx-auto">
                  {[
                    [LayoutGrid, '#D4AF37', 'Tables interactives', 'Rondes, rectangulaires, coktail…'],
                    [UserCheck,  '#5DBCBF', 'Gestion invités',      'RSVP, régimes, +1…'],
                    [Mail,       '#F4A7B2', 'Invitations',          'Design + IA + lien direct'],
                  ].map(([Icon, color, title, desc]) => (
                    <div key={title} className="card-premium p-3 text-center">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2"
                        style={{ background: `${color}18` }}>
                        <Icon size={17} style={{ color }} />
                      </div>
                      <p className="font-bold text-xs mb-0.5" style={{ color: 'var(--ink)' }}>{title}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{desc}</p>
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleCreatePlan}
                  disabled={planCreating}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-sm"
                  style={{
                    background: '#D4AF37', color: '#0B1E20', border: 'none', cursor: 'pointer',
                    boxShadow: '0 8px 24px rgba(212,175,55,0.3)',
                    opacity: planCreating ? 0.7 : 1,
                  }}>
                  {planCreating
                    ? <><Loader2 size={16} className="animate-spin" /> Création…</>
                    : <><LayoutGrid size={16} /> Créer mon plan de table</>}
                </button>
              </div>
            )}

            {/* ─── CAS 1 : plan existant ─── */}
            {!planLoading && planDeTable && planDeTable !== false && (
              <div>
                {/* Card de résumé */}
                <div className="card-premium p-6 mb-4">

                  {/* En-tête */}
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="min-w-0">
                      <h2 className="font-display text-xl font-bold truncate"
                        style={{ color: 'var(--ink)' }}>
                        {planDeTable.nom_evenement}
                      </h2>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {planDeTable.date_evenement && (
                          <span className="flex items-center gap-1 text-xs"
                            style={{ color: 'var(--text-muted)' }}>
                            <Calendar size={11} />
                            {formatDate(planDeTable.date_evenement)}
                          </span>
                        )}
                        {planDeTable.lieu && (
                          <span className="flex items-center gap-1 text-xs truncate max-w-xs"
                            style={{ color: 'var(--text-muted)' }}>
                            <Package size={11} />
                            {planDeTable.lieu}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Code public */}
                    {planDeTable.event_code && (
                      <span className="px-2.5 py-1 rounded-lg text-xs font-bold font-mono flex-shrink-0"
                        style={{ background: 'rgba(212,175,55,0.1)', color: '#C58B19' }}>
                        #{planDeTable.event_code}
                      </span>
                    )}
                  </div>

                  {/* Stats */}
                  {(() => {
                    const invites  = planDeTable.invites  || [];
                    const tables   = planDeTable.tables   || [];
                    const segs     = planDeTable.segments || [];
                    const mainSeg  = segs.find(s => s.has_plan) || segs[0];
                    const confirmes = invites.filter(i =>
                      mainSeg && i.seg_status?.[mainSeg.id] === 'yes'
                    ).length;
                    const places   = invites.filter(i => i.table_id).length;
                    const total    = planDeTable.nb_invites_total || invites.length;
                    const stats = [
                      { label: 'Invités',    value: total,     color: '#D4AF37' },
                      { label: 'Confirmés',  value: confirmes, color: '#22C55E' },
                      { label: 'Placés',     value: places,    color: '#5DBCBF' },
                      { label: 'Tables',     value: tables.length, color: '#8B5CF6' },
                    ];
                    return (
                      <div className="grid grid-cols-4 gap-2 mb-5 p-3 rounded-xl"
                        style={{ background: 'var(--bg)' }}>
                        {stats.map(s => (
                          <div key={s.label} className="text-center">
                            <p className="font-display text-2xl font-bold"
                              style={{ color: s.color }}>
                              {s.value}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              {s.label}
                            </p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* 3 boutons CTA */}
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Link
                      to="/mon-organisation/plan-de-table/editeur"
                      className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm"
                      style={{ background: '#D4AF37', color: '#0B1E20', textDecoration: 'none',
                               boxShadow: '0 6px 18px rgba(212,175,55,0.25)' }}>
                      <LayoutGrid size={15} /> Ouvrir l'éditeur
                    </Link>
                    <Link
                      to="/mon-organisation/plan-de-table/invites"
                      className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm"
                      style={{ background: '#5DBCBF', color: '#0B1E20', textDecoration: 'none' }}>
                      <Users size={15} /> Gérer mes invités
                    </Link>
                    <Link
                      to="/mon-organisation/plan-de-table/invitations"
                      className="flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm"
                      style={{ background: 'transparent', border: '1.5px solid var(--border)',
                               color: 'var(--ink)', textDecoration: 'none' }}>
                      <Mail size={15} /> Personnaliser l'invitation
                    </Link>
                  </div>
                </div>

                {/* Lien discret vers la page publique */}
                <div className="text-center">
                  <a
                    href={`/plan-de-table/${planDeTable.token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs"
                    style={{ color: 'var(--text-muted)' }}>
                    <ExternalLink size={12} />
                    Voir ma page publique
                  </a>
                </div>
              </div>
            )}
          </div>
        )}


        {activeTab === 'timeline' && (
          <div className="max-w-2xl mx-auto">
            <div className="mb-6">
              <h2 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--ink)' }}>Timeline de votre événement</h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Suivez chaque étape de votre organisation</p>
            </div>

            <div className="relative">
              {/* Ligne verticale */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5" style={{ background: 'var(--border)' }} />

              <div className="space-y-4">
                {[
                  { step: 1, label: 'Devis créé', desc: 'Votre devis a été généré', icon: '📋', done: quotes.length > 0, color: '#5DBCBF' },
                  { step: 2, label: 'Devis signé', desc: 'Signature électronique effectuée', icon: '✍️', done: quotes.some(q => ['signe','acompte_paye','confirme','solde_paye'].includes(q.statut)), color: '#D4AF37' },
                  { step: 3, label: 'Acompte payé', desc: '30% confirmé — date bloquée', icon: '💳', done: quotes.some(q => ['acompte_paye','confirme','solde_paye'].includes(q.statut)), color: '#22C55E' },
                  { step: 4, label: 'Préparation matériel', desc: 'Show Événement prépare votre commande', icon: '📦', done: false, color: '#F59E0B' },
                  { step: 5, label: 'Livraison & installation', desc: 'Votre matériel est installé', icon: '🚛', done: false, color: '#8B5CF6' },
                  { step: 6, label: 'Votre événement 🎉', desc: 'Profitez ! Nous sommes disponibles 24h/24', icon: '🎊', done: false, color: '#F4A7B2' },
                  { step: 7, label: 'Retour matériel', desc: 'Récupération par notre équipe', icon: '🔄', done: false, color: '#5DBCBF' },
                  { step: 8, label: 'Solde & caution', desc: 'Règlement final et restitution caution', icon: '✅', done: false, color: '#22C55E' },
                ].map((item, i) => (
                  <div key={i} className="relative flex items-start gap-4 pl-12">
                    {/* Point timeline */}
                    <div className="absolute left-0 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: item.done ? item.color : '#FFFFFF',
                        border: `2px solid ${item.done ? item.color : 'var(--border)'}`,
                        zIndex: 10,
                      }}>
                      {item.done ? (
                        <Check size={16} style={{ color: '#FFFFFF' }} />
                      ) : (
                        <span className="text-sm">{item.icon}</span>
                      )}
                    </div>

                    {/* Contenu */}
                    <div className="flex-1 pb-4">
                      <div className="card-premium p-4" style={{ borderLeft: `3px solid ${item.done ? item.color : 'var(--border)'}`, opacity: item.done ? 1 : 0.7 }}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{item.label}</p>
                          {item.done && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                              style={{ background: `${item.color}15`, color: item.color }}>
                              ✓ Fait
                            </span>
                          )}
                        </div>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 p-4 rounded-2xl text-center" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: '#C58B19' }}>
                📲 Suivez votre livraison en temps réel
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Le jour J, un lien de suivi vous sera envoyé par email pour suivre l'arrivée de votre matériel.
              </p>
            </div>
          </div>
        )}


        {activeTab === 'devis' && (
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-bold" style={{ color: 'var(--ink)' }}>Mes devis</h2>
              <Link to="/creer-evenement"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: '#D4AF37', color: '#0B1E20' }}>
                <Plus size={14} /> Nouveau devis
              </Link>
            </div>

            {quotes.length === 0 ? (
              <div className="text-center py-12">
                <FileText size={40} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
                <p className="font-semibold text-sm mb-3" style={{ color: 'var(--text-muted)' }}>Aucun devis pour le moment</p>
                <Link to="/creer-evenement"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold"
                  style={{ background: '#D4AF37', color: '#0B1E20' }}>
                  Créer mon premier devis <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {quotes.map(q => {
                  const statusColors = { brouillon: '#F59E0B', signe: '#1F9D7A', annule: '#EF4444', en_attente: '#5DBCBF' };
                  return (
                    <Link key={q.id} to={`/devis/${q.id}`}
                      className="card-premium p-4 flex items-center gap-4 hover:shadow-md transition-shadow">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(212,175,55,0.1)' }}>
                        <FileText size={18} style={{ color: '#D4AF37' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate" style={{ color: 'var(--ink)' }}>
                          Devis #{q.id?.slice(-6)?.toUpperCase()}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          <Clock size={10} className="inline mr-1" />
                          {formatDate(q.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sm" style={{ color: '#D4AF37' }}>{q.total?.toFixed(2)}€</p>
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{ background: `${statusColors[q.statut] || '#888'}18`, color: statusColors[q.statut] || '#888' }}>
                          {q.statut}
                        </span>
                      </div>
                      <ArrowRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>

      <Footer />
      <MobileStickyBar />
    </div>
  );
}
